import { PageRequest } from '@/core/request/PageRequest';

import {
  CreateCustomerAddressRequest,
  CustomerAddress,
} from '../CustomerAddresses/CustomerAddresses.types';

import { CreateCustomerContactRequest } from '../CustomerContacts/CustomerContacts.types';

import {
  CreateCustomerVehicleRequest,
  CustomerVehicle,
} from '../CustomerVehicles/CustomerVehicles.types';
import { Contact } from '../_shared/Contact';
import { Moment } from 'moment';
import { InvoiceTaxpayerType } from '../../invoice/invoices/invoices.enums';
import { YCarsStatus } from '@/types/YCars';
import { CustomerCategory } from '../CustomerCategory/CustomerCategory.types';
import { Country } from '@/types/Country';

export enum GenderType {
  Man = 'MAN',
  Woman = 'WOMAN',
}

export enum GenderTypeReport {
  Man = 'MAN',
  Woman = 'WOMAN',
  Genderless = 'GENDERLESS',
}

export type MaritalStatus = 'SINGLE' | 'MARRIED' | 'DIVORCED' | 'WIDOWER';

export enum PersonType {
  Natural = 'NATURAL',
  Legal = 'LEGAL',
}

export type PersonTypeValid = 'NATURAL' | 'LEGAL';

export interface Customer {
  active: boolean;
  addresses?: CustomerAddress[];
  birthDate?: string;
  contacts?: Contact[];
  cpfCnpj?: string;
  creation?: string;
  creditLimit?: number;
  enableLGPD?: boolean;
  showLGPD?: boolean;
  fantasyName?: string;
  genderType?: GenderType;
  id: number;
  maritalStatus?: MaritalStatus;
  municipalRegistration?: string;
  name: string;
  nationality?: string;
  observation?: string;
  personType: PersonType;
  profession?: string;
  rentalCompany?: boolean;
  rg?: string;
  stateRegistration?: string;
  vehicles?: CustomerVehicle[];
  website?: string;
  retentionIRPJorCSLL?: boolean;
  retentionISS?: boolean;
  recommendation?: {
    id: number;
    description: string;
    matrixId: number;
    active: boolean;
  };
}

export interface CustomerGeneral {
  active: boolean;
  birthDate: string;
  cpfCnpj: string;
  creation: string;
  creditLimit: number;
  enableLGPD: boolean;
  fantasyName: string;
  genderType: GenderType;
  id: number;
  maritalStatus: MaritalStatus;
  municipalRegistration: string;
  name: string;
  nationality: string;
  observation: string;
  personType: PersonType;
  policyNumber: string;
  profession: string;
  recommendationActive: boolean;
  recommendationDescription: string;
  recommendationId: number;
  recommendationMatrixId: number;
  rentalCompany: boolean;
  retentionIRPJorCSLL: boolean;
  retentionISS: boolean;
  rg: string;
  showLGPD: boolean;
  stateRegistration: string;
  website: string;
  taxpayerType?: InvoiceTaxpayerType;
  ycarsStatus?: YCarsStatus;
  category?: CustomerCategory;
  michelinSegmentCode?: string;
  foreignDocument?: string;
}

export interface ListCustomersQuery extends PageRequest {
  active?: boolean;
  cpfCnpj?: string;
  fantasyName?: string;
  genderType?: GenderType;
  licensePlate?: string;
  personType?: PersonType;
  activeVehicle?: boolean;
  endBirthDate?: string;
  search?: string;
  startBirthDate?: string;
  contact?: string;
}

export interface ListCustomersReducedQuery extends PageRequest {
  active?: boolean;
  search?: string;
  cpfCnpj?: string;
  fantasyName?: string;
  genderType?: GenderType;
  licensePlate?: string;
  personType?: PersonType;
  contact?: string;
}

export interface CustomerReduced {
  id: number;
  name: string;
  cpfCnpj?: string;
  foreignDocument?: string;
  creation?: string;
  birthDate?: string;
  fantasyName?: string;
  category?: CustomerCategory;
  isPublicAgency?: boolean;
}

export interface CreateCustomerRequest {
  active: true;
  addresses?: CreateCustomerAddressRequest[];
  birthDate?: string;
  contacts?: CreateCustomerContactRequest[];
  cpfCnpj?: string;
  creation?: string;
  creditLimit?: number;
  fantasyName?: string;
  genderType?: GenderType;
  maritalStatus?: MaritalStatus;
  municipalRegistration?: string;
  policyNumber?: string;
  name: string;
  nationality?: string;
  observation?: string;
  personType: PersonType;
  profession?: string;
  rentalCompany?: boolean;
  rg?: string;
  stateRegistration?: string;
  vehicles?: CreateCustomerVehicleRequest[];
  website?: string;
  recommendation?: {
    id: number;
  };
  foreignDocument?: string;
  countryOrigin?: Country;
}

export interface CustomerFormValues {
  michelinSegmentCode?: string;
  active: boolean;
  enableLGPD?: boolean;
  addresses?: CreateCustomerAddressRequest[];
  birthDate?: Moment;
  contacts?: CreateCustomerContactRequest[];
  cpfCnpj?: string;
  creation?: Moment;
  creditLimit?: number;
  fantasyName?: string;
  genderType?: GenderType;
  maritalStatus?: MaritalStatus;
  municipalRegistration?: string;
  policyNumber?: string;
  name: string;
  nationality?: string;
  observation?: string;
  personType: PersonType;
  profession?: string;
  rentalCompany?: boolean;
  rg?: string;
  stateRegistration?: string;
  vehicles?: CreateCustomerVehicleRequest[];
  website?: string;
  isPublicAgency?: boolean;
  recommendation?: {
    id: number;
  };
  address?: {
    cityId?: number;
    city?: string;
    complement?: string;
    district?: string;
    number?: string;
    street?: string;
    zipCode?: string;
    state?: string;
  };
  foreignDocument?: string;
  countryOrigin?: Country;
}

export interface UpdateCustomerRequest {
  active: boolean;
  addresses?: CreateCustomerAddressRequest[];
  birthDate?: string;
  contacts?: CreateCustomerContactRequest[];
  cpfCnpj?: string;
  creation?: string;
  creditLimit?: number;
  fantasyName?: string;
  genderType?: GenderType;
  maritalStatus?: MaritalStatus;
  municipalRegistration?: string;
  name: string;
  nationality?: string;
  observation?: string;
  personType: PersonType;
  profession?: string;
  rentalCompany?: boolean;
  rg?: string;
  stateRegistration?: string;
  vehicles?: CreateCustomerVehicleRequest[];
  website?: string;
  recommendation?: {
    id?: number;
  };
}

export interface CustomerLGPDActive {
  id: number;
  enableLgpd: boolean | null;
}

export interface CustomerRegisterInterface {
  date: string;
  male: number;
  averageMaleAge: number;
  female: number;
  averageFemaleAge: number;
  averageNoGenderAge: number;
  total: number;
}

export interface CustomerIndicatorInterface {
  customerAge: CustomerIndicatorAgeInterface;
  customerGender: CustomerIndicatorGenderInterface;
}

export interface DateTreated {
  genderUnspecifiedCustomersCount?: number;
  maleCustomersCount?: number;
  femaleCustomersCount?: number;
  averageAgeMaleCustomers?: number;
  averageAgeFemaleCustomers?: number;
  averageAgeGenderUnspecifiedCustomers?: number;
}

export enum GranularityType {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export const granularityTranslator = {
  [GranularityType.DAY]: 'Dias',
  [GranularityType.WEEK]: 'Semanas',
  [GranularityType.MONTH]: 'Meses',
  [GranularityType.YEAR]: 'anos',
};

export interface CustomerIndicatorForm {
  endDate?: Moment;
  initialDate?: Moment;
  granularity?: GranularityType;
}

export interface CustomerIndicatorRequest {
  endDate?: string;
  initialDate?: string;
  granularity?: GranularityType;
  order?: 'ASC' | 'DESC';
}

export interface CustomerIndicatorAgeInterface {
  averageAgeFemaleCustomers: number;
  averageAgeGenderUnspecifiedCustomers: number;
  averageAgeMaleCustomers: number;
}

export interface CustomerIndicatorGenderInterface {
  femaleCustomersCount: number;
  genderUnspecifiedCustomersCount: number;
  maleCustomersCount: number;
}

export interface CustomerVehiclesFilterReducedRequest extends PageRequest {
  active?: boolean;
  brandId?: number;
  customerId?: number;
  licensePlate?: string;
  modelId?: number;
  vehicleId?: number;
}

export interface CustomerCpfCnpjExistsResponse {
  exists: boolean;
}

export interface CustomerUpdateCategoryRequest {
  id: number;
  categoryId?: number;
}

export interface CustomerPortoIsValidData {
  isValidPortoCustomer: boolean;
  missingFields: string[];
}
